import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage, useFormikContext } from 'formik';
import { Card, InputGroup } from 'react-bootstrap';
import UserBonusList from '../../components/Bonuses/UserBonusList';
import { monthOptions, halfyearOptions, quarterOptions } from '../../utils/formatBonus';

const ManualBonusForm: React.FC<{
  manualBonusId: any;
  bonusData: any;
  isSubmitting: boolean;
  isDirty: boolean;
  isDisabled?: boolean;
}> = ({ manualBonusId, bonusData, isSubmitting, isDirty, isDisabled = false }) => {
  const { setValues, values } = useFormikContext<any>();
  const navigate = useNavigate();
  const manualBonusUsers = values.manualBonusUsers || [];

  useEffect(() => {
    setValues(bonusData);
  }, [bonusData]);

  return (
    <Form>
      <h3 className="text-dark mb-4">Manual Bonus</h3>
      <div className={manualBonusId === 'create' ? 'w-75 m-auto ' : 'row mb-3'}>
        <div className="col">
          <Card>
            <p className="text-primary px-2 mx-3 pt-3 fw-bold">Bonus Settings</p>
            <div className="row mx-2 px-1">
              <div className="col">
                <div className="mb-3">
                  <label htmlFor="periodType" className="col-form-label">
                    Period Type
                  </label>
                  <Field
                    as="select"
                    name="periodType"
                    className="form-control"
                    onChange={(e) => {
                      const selectedPeriod = e.target.value;
                      const defaultMonthBitmask = {
                        month: monthOptions[0].value, // January
                        quarter: quarterOptions[0].value, // Q1
                        halfyear: halfyearOptions[0].value, // H1
                        year: 0b111111111111, // Full year
                      };
                      setValues({
                        ...values,
                        periodType: e.target.value,
                        monthBitmask: defaultMonthBitmask[selectedPeriod as keyof typeof defaultMonthBitmask],
                      });
                    }}
                    disabled={isDisabled}
                  >
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                    <option value="halfyear">6 Months</option>
                    <option value="year">Year</option>
                  </Field>
                  <ErrorMessage name="periodType" component="div" className="text-danger" />
                </div>
              </div>
              <div className="col">
                <div className="mb-3">
                  <label htmlFor="year" className="col-form-label">
                    Year
                  </label>
                  <Field
                    name="year"
                    type="number"
                    className="form-control"
                    min={2020}
                    max={2100}
                    disabled={isDisabled}
                  />
                  <ErrorMessage name="year" component="div" className="text-danger" />
                </div>
              </div>
            </div>
            {/* Dynamic period input */}
            <div className="row mx-2 px-1">
              {values.periodType === 'month' && (
                <div className="col">
                  <div className="mb-3">
                    <label htmlFor="monthBitmask" className="col-form-label">
                      Month
                    </label>
                    <Field as="select" name="monthBitmask" className="form-control" disabled={isDisabled}>
                      {monthOptions.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="month" component="div" className="text-danger" />
                  </div>
                </div>
              )}
              {values.periodType === 'quarter' && (
                <div className="col">
                  <div className="mb-3">
                    <label htmlFor="monthBitmask" className="col-form-label">
                      Quarter
                    </label>
                    <Field as="select" name="monthBitmask" className="form-control" disabled={isDisabled}>
                      {quarterOptions.map((q) => (
                        <option key={q.value} value={q.value}>
                          {q.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="quarter" component="div" className="text-danger" />
                  </div>
                </div>
              )}
              {values.periodType === 'halfyear' && (
                <div className="col">
                  <div className="mb-3">
                    <label htmlFor="monthBitmask" className="col-form-label">
                      Half
                    </label>
                    <Field as="select" name="monthBitmask" className="form-control">
                      {halfyearOptions.map((h) => (
                        <option key={h.value} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="half" component="div" className="text-danger" />
                  </div>
                </div>
              )}
            </div>
            <div className="row mx-2 px-1">
              <div className="col">
                <div className="mb-3">
                  <label htmlFor="description" className="col-form-label">
                    Description
                  </label>
                  <Field name="description" type="text" className="form-control" disabled={isDisabled} />
                  <ErrorMessage name="description" component="div" className="text-danger" />
                </div>
              </div>
            </div>
            {/* User bonus list */}
            <UserBonusList
              manualBonusUsers={manualBonusUsers}
              values={values}
              setValues={setValues}
              disabled={isDisabled}
            />
            {!isDisabled && (
              <div className="my-4 d-flex justify-content-center gap-3">
                <button className="btn btn-primary btn-md" type="submit" disabled={isSubmitting || !isDirty}>
                  {manualBonusId ? 'Save Bonus' : 'Create Bonus'}
                </button>
                <button className="btn btn-secondary btn-md" type="button" onClick={() => navigate('/manual-bonus')}>
                  Cancel
                </button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Form>
  );
};

export default ManualBonusForm;
